import { useState, useEffect } from "react";
import theme from "../data/theme";

export default function LandingPage({ onLaunch, onOpenAlmanac, onAbout, onOpenLiveBoard, liveBoardEnabled = false, rateEnabled = true }) {
  const [visible, setVisible] = useState(false);
  const [hovering, setHovering] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 820);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 820);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const cardStyle = (id) => ({
    flex: 1,
    background: hovering === id ? theme.helpers.brandBg(0.10) : theme.colors.bg.raised,
    border: `1px solid ${hovering === id ? "rgba(255,107,0,0.5)" : theme.colors.border.default}`,
    borderRadius: 14,
    padding: "24px 20px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 10,
    cursor: "pointer",
    transform: hovering === id ? "scale(1.02)" : "scale(1)",
    transition: "all 0.2s ease",
    minWidth: 0,
  });

  const cardBtnStyle = {
    background: "transparent",
    color: theme.colors.brand.accent,
    border: `1px solid rgba(255,107,0,0.5)`,
    padding: "10px 24px",
    fontSize: 13,
    fontWeight: 800,
    fontFamily: theme.fonts.display,
    letterSpacing: "2px",
    borderRadius: 10,
    cursor: "pointer",
    transition: "all 0.2s ease",
    width: "100%",
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: `radial-gradient(ellipse at 50% 0%, #1a1f35 0%, ${theme.colors.bg.deep} 50%, ${theme.colors.bg.app} 100%)`,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "40px 24px",
      fontFamily: theme.fonts.display,
      overflow: "hidden",
      position: "relative",
    }}>
      {/* Ambient glow */}
      <div style={{
        position: "absolute",
        top: "-20%",
        left: "50%",
        transform: "translateX(-50%)",
        width: "600px",
        height: "400px",
        background: `radial-gradient(ellipse, ${theme.helpers.brandBg()} 0%, transparent 70%)`,
        pointerEvents: "none",
      }} />

      {/* Content */}
      <div style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(20px)",
        transition: "all 0.8s cubic-bezier(0.16, 1, 0.3, 1)",
        textAlign: "center",
        maxWidth: 620,
        width: "100%",
        position: "relative",
        zIndex: 1,
      }}>
        {/* Logo */}
        <svg viewBox="0 0 300 78" style={{ width: 280, height: 73, display: "block", margin: "0 auto 8px" }} xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="lp-grad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor={theme.colors.brand.orange} />
              <stop offset="100%" stopColor={theme.colors.brand.accent} />
            </linearGradient>
            <linearGradient id="lp-line" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor={theme.colors.brand.accent} stopOpacity="0.7" />
              <stop offset="100%" stopColor={theme.colors.brand.accent} stopOpacity="0" />
            </linearGradient>
          </defs>
          <g transform="translate(61,0)">
            <rect x="168" y="6" width="55" height="2.5" fill="url(#lp-line)" opacity="0.5" rx="1" />
            <rect x="168" y="14" width="60" height="2.5" fill="url(#lp-line)" opacity="0.8" rx="1" />
            <rect x="168" y="22" width="50" height="2" fill="url(#lp-line)" opacity="0.6" rx="1" />
            <rect x="168" y="29" width="45" height="2" fill="url(#lp-line)" opacity="0.4" rx="1" />
            <rect x="168" y="36" width="55" height="2.5" fill="url(#lp-line)" opacity="0.5" rx="1" />
            <text y="38" fontFamily="'Outfit', sans-serif" fontWeight="900" fontSize="44" letterSpacing="-1">
              <tspan fill="#ffffff">MOCK</tspan><tspan fill="url(#lp-grad)"> 1</tspan>
            </text>
            <text x="2" y="55" fontFamily="'Outfit', sans-serif" fontWeight="600" fontSize="12" fill="#ffffff" letterSpacing="4.5" opacity="0.9">AUCTION SUITE</text>
            <text x="2" y="71" fontFamily="'Outfit', sans-serif" fontWeight="500" fontSize="8.5" fill={theme.colors.text.muted} letterSpacing="1.5">POWERED BY REAL AUCTION DATA</text>
          </g>
        </svg>

        {/* Hero tagline */}
        <h1 style={{
          fontSize: isMobile ? 22 : 28,
          color: "#e2e8f0",
          lineHeight: 1.4,
          margin: "28px 0 8px",
          fontWeight: 700,
          fontFamily: theme.fonts.display,
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0)" : "translateY(12px)",
          transition: "all 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.15s",
        }}>
          Mock draft against opponents who{" "}
          <span style={{ color: theme.colors.brand.accent }}>bid like real people</span>
        </h1>

        <p style={{
          fontSize: 13,
          color: theme.colors.text.secondary,
          letterSpacing: "0.5px",
          margin: "0 0 28px",
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0)" : "translateY(12px)",
          transition: "all 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.25s",
        }}>
          Calibrated from real auction data. Free to use. No sign-up required.
        </p>

        {/* Primary CTA */}
        <div style={{
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0)" : "translateY(12px)",
          transition: "all 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.35s",
          marginBottom: 32,
        }}>
          <button
            onClick={() => onLaunch("mock")}
            onMouseEnter={() => setHovering("mock")}
            onMouseLeave={() => setHovering(null)}
            style={{
              background: hovering === "mock"
                ? "linear-gradient(135deg, #FF8C00, #FF6B00)"
                : theme.colors.brand.gradientBtn,
              color: "#fff",
              border: "2px solid transparent",
              padding: "18px 48px",
              fontSize: 17,
              fontWeight: 800,
              fontFamily: theme.fonts.display,
              letterSpacing: "2.5px",
              borderRadius: 14,
              cursor: "pointer",
              boxShadow: hovering === "mock"
                ? "0 8px 32px rgba(255,107,0,0.4), 0 0 0 1px rgba(255,149,0,0.3)"
                : "0 4px 20px rgba(255,107,0,0.25)",
              transform: hovering === "mock" ? "scale(1.03)" : "scale(1)",
              transition: "all 0.2s ease",
              width: isMobile ? "100%" : "auto",
              minWidth: isMobile ? 0 : 280,
            }}
          >
            START MOCK DRAFT
          </button>
        </div>

        {/* Secondary cards row */}
        <div style={{
          display: "flex",
          gap: 14,
          flexDirection: isMobile ? "column" : "row",
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0)" : "translateY(12px)",
          transition: "all 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.45s",
          marginBottom: 14,
        }}>
          <div
            style={cardStyle("tiers")}
            onClick={() => onLaunch("tiers")}
            onMouseEnter={() => setHovering("tiers")}
            onMouseLeave={() => setHovering(null)}
          >
            <span style={{ fontSize: 14, fontWeight: 800, color: theme.colors.text.primary, letterSpacing: "1.5px" }}>TIER BUILDER</span>
            <span style={{ fontSize: 12, color: theme.colors.text.secondary, lineHeight: 1.4 }}>Drag-and-drop custom tier sheets for draft day</span>
            <button style={cardBtnStyle} onClick={(e) => { e.stopPropagation(); onLaunch("tiers"); }}>OPEN</button>
          </div>
          <div
            style={cardStyle("builder")}
            onClick={() => onLaunch("builder")}
            onMouseEnter={() => setHovering("builder")}
            onMouseLeave={() => setHovering(null)}
          >
            <span style={{ fontSize: 14, fontWeight: 800, color: theme.colors.text.primary, letterSpacing: "1.5px" }}>TEAM BUILDER</span>
            <span style={{ fontSize: 12, color: theme.colors.text.secondary, lineHeight: 1.4 }}>Build rosters, compare values, plan your strategy</span>
            <button style={cardBtnStyle} onClick={(e) => { e.stopPropagation(); onLaunch("builder"); }}>OPEN</button>
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════════
            LIVE BOARD card — the Almanac card's markup, deliberately.
            Same row shape, same padding, same Beta pill, same OPEN button, so
            the two read as one row style rather than two designs.

            🔴 GATED ON THE SAME FLAG AS THE NAV TAB AND THE /live ROUTE. This
            is a THIRD entry point to the same surface; liveBoardFlag.js exists
            so `?live=0` pulls the beta from a browser without a deploy, and an
            ungated card here would quietly undo that.

            The sample link is NOT gated and does not need to be: /watch is a
            public read-only path routed in App.js before the flag is ever read.
            It is a real anchor so it opens in a new tab and can be copied.
            ══════════════════════════════════════════════════════════════ */}
        {liveBoardEnabled && (
          <div
            style={{
              ...cardStyle("liveboard"),
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 16,
              padding: "16px 20px",
              opacity: visible ? 1 : 0,
              transform: visible ? "translateY(0)" : "translateY(12px)",
              transition: "all 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.5s",
              marginBottom: 14,
            }}
            onClick={() => onOpenLiveBoard && onOpenLiveBoard()}
            onMouseEnter={() => setHovering("liveboard")}
            onMouseLeave={() => setHovering(null)}
          >
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4, minWidth: 0, textAlign: "left" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 14, fontWeight: 800, color: theme.colors.text.primary, letterSpacing: "1.5px" }}>
                LIVE BOARD
                <span style={{
                  fontSize: theme.fontSize.nano, fontWeight: theme.fontWeight.heavy,
                  letterSpacing: "0.5px", color: theme.colors.brand.orange,
                  background: theme.helpers.brandBg(0.12), border: `1px solid ${theme.colors.border.brandLight}`,
                  borderRadius: 4, padding: "1px 4px", lineHeight: 1,
                }}>BETA</span>
              </span>
              <span style={{ fontSize: 12, color: theme.colors.text.secondary, lineHeight: 1.4 }}>
                Run your real auction on it. Type each sale, your league watches the board live on any screen.
              </span>
              {/* stopPropagation: the whole card is clickable, and a sample is
                  not the thing the card is for. */}
              <a
                href="/watch/sample"
                onClick={(e) => e.stopPropagation()}
                style={{
                  fontSize: 11, color: theme.colors.text.faint, textDecoration: "none",
                  letterSpacing: "0.5px", borderBottom: `1px solid ${theme.colors.border.default}`,
                  paddingBottom: 1,
                }}
                onMouseEnter={(e) => e.target.style.color = theme.colors.text.secondary}
                onMouseLeave={(e) => e.target.style.color = theme.colors.text.faint}
              >See a sample board</a>
            </div>
            <button style={{ ...cardBtnStyle, width: "auto", padding: "8px 20px", flexShrink: 0 }} onClick={(e) => { e.stopPropagation(); onOpenLiveBoard && onOpenLiveBoard(); }}>OPEN</button>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════
            RATE MY AUCTION card — the LIVE BOARD card's markup, deliberately,
            so the rows read as one design. Two deliberate differences, and one
            thing it now shares:

            🔴 GATED, AND DEFAULT ON SINCE SEP 4 2026 (rateFlag.js). This is one
            of /rate's two discovery surfaces — the other is the cross-link on
            /submit — and both shipped to everyone at launch. `?rate=0` pulls
            them for a browser without a deploy; `?rate=1` is now a no-op on a
            stock profile. The prop DEFAULTS TO TRUE to match the flag: a caller
            that forgets to wire it gets the launched state, not the stealth one.
            The /rate ROUTE is deliberately not gated and never was: a direct
            link has to keep working, so App.js matches the pathname before the
            flag is ever read.

            BETA PILL: added Sep 4 2026 at Ben's call, using the exact markup
            the DRAFT PLAN and LIVE BOARD cards carry, so all three pills on the
            page are identical.

            A REAL ANCHOR, not an appMode switch: /rate is a standalone
            surface outside the builder shell (App.js routes it before the
            shell mounts), so the whole card is an <a> — it right-click-opens
            in a new tab and can be copied, the way /watch/sample can. The
            OPEN affordance is a <span> wearing cardBtnStyle rather than a
            <button>: interactive content cannot nest inside an anchor, and
            the anchor is the thing that has to stay real.
            ══════════════════════════════════════════════════════════════ */}
        {rateEnabled && (
        <a
          href="/rate"
          data-testid="landing-rate-card"
          style={{
            ...cardStyle("rate"),
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 16,
            padding: "16px 20px",
            textDecoration: "none",
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(12px)",
            transition: "all 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.5s",
            marginBottom: 14,
          }}
          onMouseEnter={() => setHovering("rate")}
          onMouseLeave={() => setHovering(null)}
        >
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4, minWidth: 0, textAlign: "left" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 14, fontWeight: 800, color: theme.colors.text.primary, letterSpacing: "1.5px" }}>
              RATE MY AUCTION
              <span style={{
                fontSize: theme.fontSize.nano, fontWeight: theme.fontWeight.heavy,
                letterSpacing: "0.5px", color: theme.colors.brand.orange,
                background: theme.helpers.brandBg(0.12), border: `1px solid ${theme.colors.border.brandLight}`,
                borderRadius: 4, padding: "1px 4px", lineHeight: 1,
              }}>BETA</span>
            </span>
            <span style={{ fontSize: 12, color: theme.colors.text.secondary, lineHeight: 1.4 }}>
              Already drafted? Rate your whole league or just your team. Paste your draft results or link your Sleeper league, and every seat gets a grade, projected points, and the price the room paid vs real 2026 auctions.
            </span>
          </div>
          <span style={{ ...cardBtnStyle, width: "auto", padding: "8px 20px", flexShrink: 0, display: "inline-block" }}>OPEN</span>
        </a>
        )}

        {/* ══════════════════════════════════════════════════════════════
            DRAFT PLAN card — the LIVE BOARD card's markup, deliberately, and
            it sits directly below it so the two betas read as one row style.
            Not gated: Draft Plan has no flag, it is a beta by label only.
            ══════════════════════════════════════════════════════════════ */}
        <div
          style={{
            ...cardStyle("plan"),
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 16,
            padding: "16px 20px",
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(12px)",
            transition: "all 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.5s",
            marginBottom: 14,
          }}
          onClick={() => onLaunch("plan")}
          onMouseEnter={() => setHovering("plan")}
          onMouseLeave={() => setHovering(null)}
        >
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4, minWidth: 0, textAlign: "left" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 14, fontWeight: 800, color: theme.colors.text.primary, letterSpacing: "1.5px" }}>
              DRAFT PLAN
              <span style={{
                fontSize: theme.fontSize.nano, fontWeight: theme.fontWeight.heavy,
                letterSpacing: "0.5px", color: theme.colors.brand.orange,
                background: theme.helpers.brandBg(0.12), border: `1px solid ${theme.colors.border.brandLight}`,
                borderRadius: 4, padding: "1px 4px", lineHeight: 1,
              }}>BETA</span>
            </span>
            <span style={{ fontSize: 12, color: theme.colors.text.secondary, lineHeight: 1.4 }}>
              Build your target roster at real prices and print it for draft night.
            </span>
          </div>
          <button style={{ ...cardBtnStyle, width: "auto", padding: "8px 20px", flexShrink: 0 }} onClick={(e) => { e.stopPropagation(); onLaunch("plan"); }}>OPEN</button>
        </div>

        {/* League Almanac card — full feature, works on mobile (no DESKTOP ONLY tag) */}
        <div
          style={{
            ...cardStyle("almanac"),
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 16,
            padding: "16px 20px",
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(12px)",
            transition: "all 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.5s",
            marginBottom: 14,
          }}
          onClick={() => onOpenAlmanac && onOpenAlmanac()}
          onMouseEnter={() => setHovering("almanac")}
          onMouseLeave={() => setHovering(null)}
        >
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4, minWidth: 0, textAlign: "left" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 14, fontWeight: 800, color: theme.colors.text.primary, letterSpacing: "1.5px" }}>
              LEAGUE ALMANAC
              <span style={{
                fontSize: theme.fontSize.nano, fontWeight: theme.fontWeight.heavy,
                letterSpacing: "0.5px", color: theme.colors.brand.orange,
                background: theme.helpers.brandBg(0.12), border: `1px solid ${theme.colors.border.brandLight}`,
                borderRadius: 4, padding: "1px 4px", lineHeight: 1,
              }}>BETA</span>
            </span>
            <span style={{ fontSize: 12, color: theme.colors.text.secondary, lineHeight: 1.4 }}>Import your league's history — records, rivalries, and 20 years of receipts</span>
          </div>
          <button style={{ ...cardBtnStyle, width: "auto", padding: "8px 20px", flexShrink: 0 }} onClick={(e) => { e.stopPropagation(); onOpenAlmanac && onOpenAlmanac(); }}>OPEN</button>
        </div>

        {/* Projection Lab card */}
        <div
          style={{
            ...cardStyle("projections"),
            cursor: isMobile ? "default" : "pointer",
            pointerEvents: isMobile ? "none" : "auto",
            position: "relative",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 20px",
            opacity: visible ? (isMobile ? 0.45 : 1) : 0,
            transform: visible ? "translateY(0)" : "translateY(12px)",
            transition: "all 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.55s",
          }}
          onClick={() => !isMobile && onLaunch("projections")}
          onMouseEnter={() => !isMobile && setHovering("projections")}
          onMouseLeave={() => setHovering(null)}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 14, fontWeight: 800, color: theme.colors.text.primary, letterSpacing: "1.5px" }}>PROJECTION LAB</span>
            <span style={{ fontSize: 12, color: theme.colors.text.secondary }}>Project stats for all 32 NFL teams</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{
              fontSize: 10,
              fontWeight: 700,
              color: isMobile ? theme.colors.text.muted : theme.colors.brand.accent,
              background: isMobile ? "rgba(255,255,255,0.08)" : "rgba(255,107,0,0.12)",
              padding: "4px 10px",
              borderRadius: 6,
              letterSpacing: "1px",
            }}>
              DESKTOP ONLY
            </span>
            {!isMobile && (
              <button style={{ ...cardBtnStyle, width: "auto", padding: "8px 20px" }} onClick={(e) => { e.stopPropagation(); onLaunch("projections"); }}>OPEN</button>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          marginTop: 48,
          opacity: visible ? 1 : 0,
          transition: "all 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.65s",
          display: "flex",
          justifyContent: "center",
          flexWrap: "wrap",
          gap: 18,
          alignItems: "center",
        }}>
          <span
            onClick={() => onAbout && onAbout()}
            style={{
              fontSize: 12,
              color: theme.colors.text.faint,
              textDecoration: "none",
              letterSpacing: "0.5px",
              cursor: "pointer",
            }}
            onMouseEnter={(e) => e.target.style.color = theme.colors.text.secondary}
            onMouseLeave={(e) => e.target.style.color = theme.colors.text.faint}
          >
            About
          </span>
          <a
            href="https://buymeacoffee.com/mock1"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: 12,
              color: theme.colors.brand.accent,
              textDecoration: "none",
              letterSpacing: "0.5px",
            }}
            onMouseEnter={(e) => e.target.style.opacity = "0.75"}
            onMouseLeave={(e) => e.target.style.opacity = "1"}
          >
            ☕ Buy me a coffee
          </a>
          <a
            href="https://forms.gle/ExaXAfb3XCC2oLVc8"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: 12,
              color: theme.colors.text.faint,
              textDecoration: "none",
              letterSpacing: "0.5px",
            }}
            onMouseEnter={(e) => e.target.style.color = theme.colors.text.secondary}
            onMouseLeave={(e) => e.target.style.color = theme.colors.text.faint}
          >
            Feedback?
          </a>
          <a
            href="mailto:mock1app@gmail.com"
            style={{
              fontSize: 12,
              color: theme.colors.text.faint,
              textDecoration: "none",
              letterSpacing: "0.5px",
            }}
            onMouseEnter={(e) => e.target.style.color = theme.colors.text.secondary}
            onMouseLeave={(e) => e.target.style.color = theme.colors.text.faint}
          >
            Contact
          </a>
          <span style={{
            fontSize: 11,
            color: theme.colors.border.default,
            letterSpacing: "1px",
          }}>
            &copy; 2026 MOCK 1
          </span>
        </div>
      </div>
    </div>
  );
}
